import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  type Transformer,
} from '@lexical/markdown';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import type { InitialConfigType } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin as LexicalOnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import classNames from 'classnames';
import { $createParagraphNode, $getRoot } from 'lexical';
import type { EditorState, LexicalEditor } from 'lexical';
import 'prismjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism-tomorrow.css';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FloatingLinkEditorPlugin from './FloatingLinkEditorPlugin';
import {
  createMarkdownEditorConfig,
  createMarkdownTransformers,
  createMarkdownUrlMatchers,
} from './MarkdownEditor.config';
import { ViewMode } from './MarkdownEditor.types';
import { normalizeMdForCompare, unescapeMarkdownOutput } from './MarkdownEditor.utils';
import Toolbar from './Toolbar';
import { CodeBlockExitPlugin } from './plugins/CodeBlockExitPlugin';
import { DefaultCodeLanguagePlugin } from './plugins/DefaultCodeLanguagePlugin';
import { ImagePasteBlockerPlugin } from './plugins/ImagePasteBlockerPlugin';
import { KeyboardShortcutPlugin } from './plugins/KeyboardShortcutPlugin';
import { ListNormalizationPlugin } from './plugins/ListNormalizationPlugin';
import { MarkdownPastePlugin } from './plugins/MarkdownPastePlugin';
import { PrismHighlightPlugin } from './plugins/PrismHighlightPlugin';
import { validateUrl } from './utils/sanitizeUrl';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';

type Props = {
  field: ControllerRenderProps<any, string>;
  placeholder: string;
  onContentNormalized?: (value: string) => void;
  disabled?: boolean;
};

function Placeholder({ text }: { text: string }) {
  return (
    <div className="pointer-events-none absolute left-0 top-0 select-none px-4 py-3 text-sm text-foreground-subtle">
      {text}
    </div>
  );
}

/**
 * MarkdownEditor: A dual-mode editor with RichText and PlainText tabs
 *
 * STORY:
 * - This editor has 2 tabs: RICH_TEXT (visual editor) and PLAIN_TEXT (raw markdown)
 * - PlainText is the source of truth - it's stored in the form field
 * - When you edit RichText → changes sync to PlainText
 * - When you edit PlainText → changes sync to RichText
 * - ViewMode controls which tab is active and which direction the sync happens
 */

/**
 * Helper: Convert editor state to markdown string
 * Unified helper used by both sync directions
 * Includes unescaping to ensure proper markdown output
 */
function convertEditorToMarkdown(editor: LexicalEditor, transformers: Transformer[]): string {
  const markdown = editor.getEditorState().read(() => $convertToMarkdownString(transformers));
  return unescapeMarkdownOutput(markdown);
}

/**
 * Helper: Check if two markdown strings are semantically different
 * Returns true if they ARE different (need to update)
 */
function hasMarkdownChanged(markdownA: string, markdownB: string): boolean {
  return normalizeMdForCompare(markdownA) !== normalizeMdForCompare(markdownB);
}

type EditorContentProps = {
  viewMode: ViewMode;
  plainTextValue: string | undefined;
  onPlainTextChange: (value: string) => void;
  onPlainTextBlur: () => void;
  remainingPlainTextFieldParams: Record<string, any>;
  transformers: Transformer[];
  urlMatchers: Array<(text: string) => any>;
  placeholder: string;
  containerRef: React.RefObject<HTMLDivElement>;
  onContentNormalized?: (value: string) => void;
  disabled?: boolean;
};

/**
 * Inner component that uses Lexical context
 * Handles the two-way sync between PlainText ↔ RichText
 */
function EditorContent({
  viewMode,
  plainTextValue,
  onPlainTextChange,
  onPlainTextBlur,
  remainingPlainTextFieldParams,
  transformers,
  urlMatchers,
  placeholder,
  containerRef,
  onContentNormalized,
  disabled: disabledProp,
}: EditorContentProps) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation(['knowledge_base']);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const skipNextRichTextSyncRef = useRef(false);
  const lastSyncedMarkdownRef = useRef<string | null>(null);
  const pendingNormalizationRef = useRef(false);
  const disabled = disabledProp || !!remainingPlainTextFieldParams.disabled;
  const lockedTitle = t('knowledge_base:auto_refresh.locked_banner');

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  // Respect disabled state in RichText editor
  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  /**
   * Sync RichText → PlainText
   * When user edits RichText, update the PlainText form field
   */
  const handleRichTextChange = useCallback(
    (_editorState: EditorState) => {
      if (skipNextRichTextSyncRef.current) {
        skipNextRichTextSyncRef.current = false;
        return;
      }
      // Only sync when user is actively editing RichText tab
      if (viewMode !== ViewMode.RICH_TEXT) return;

      const richTextValue = convertEditorToMarkdown(editor, transformers);

      // Clear any pending debounced update
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the update to PlainText field
      debounceTimerRef.current = setTimeout(() => {
        onPlainTextChange(richTextValue);
        lastSyncedMarkdownRef.current = richTextValue;
      }, 150);
    },
    [editor, viewMode, transformers, onPlainTextChange],
  );

  /**
   * Sync PlainText → RichText
   * When user edits PlainText tab, update the RichText editor
   */
  useEffect(() => {
    const plainText = plainTextValue ?? '';
    const richTextValue = convertEditorToMarkdown(editor, transformers);

    // If content is the same, don't update
    if (!hasMarkdownChanged(plainText, richTextValue)) {
      lastSyncedMarkdownRef.current = plainText;
      return;
    }

    // Only update when user is viewing PlainText tab OR editor is empty/initial load
    const isRichTextEditorEmpty = richTextValue.trim() === '';
    const isInitialLoad =
      lastSyncedMarkdownRef.current === null || lastSyncedMarkdownRef.current === '';
    if (!isRichTextEditorEmpty && !isInitialLoad && viewMode === ViewMode.RICH_TEXT) return;

    // Update RichText editor with PlainText content
    skipNextRichTextSyncRef.current = true;
    pendingNormalizationRef.current = true;
    // No selectEnd/selectStart here: that would auto-scroll the contenteditable to the caret.
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      $convertFromMarkdownString(plainText, transformers, undefined, true);
      if (root.getChildrenSize() === 0) {
        root.append($createParagraphNode());
      }
    });
    queueMicrotask(() => {
      if (!pendingNormalizationRef.current) return;
      if (!plainText.trim()) {
        pendingNormalizationRef.current = false;
        return;
      }
      try {
        const normalizedContent = convertEditorToMarkdown(editor, transformers);
        lastSyncedMarkdownRef.current = normalizedContent;
        if (
          onContentNormalized &&
          viewMode !== ViewMode.PLAIN_TEXT &&
          hasMarkdownChanged(plainText, normalizedContent)
        ) {
          onContentNormalized(normalizedContent);
        }
      } finally {
        pendingNormalizationRef.current = false;
      }
    });
  }, [editor, plainTextValue, transformers, viewMode, onContentNormalized]);

  return (
    <div
      ref={containerRef}
      className="relative flex-1 min-h-[200px] overflow-hidden"
      title={disabled ? lockedTitle : undefined}
    >
      {viewMode === ViewMode.RICH_TEXT ? (
        <>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                role="textbox"
                aria-multiline="true"
                data-testid="article-body-editor"
                className={classNames(
                  '!custom-scroll h-full w-full resize-none overflow-auto bg-card px-4 py-3 text-sm text-foreground outline-none',
                  disabled && 'cursor-not-allowed',
                )}
                aria-label={placeholder}
                spellCheck
                onBlur={onPlainTextBlur}
              />
            }
            placeholder={<Placeholder text={placeholder} />}
            ErrorBoundary={({ children }) => <>{children}</>}
          />
          {/* Sync RichText → PlainText */}
          <LexicalOnChangePlugin onChange={handleRichTextChange} />
          <HistoryPlugin />
          <PrismHighlightPlugin />
          <DefaultCodeLanguagePlugin />
          <ListPlugin />
          <ListNormalizationPlugin />
          <LinkPlugin validateUrl={validateUrl} />
          <AutoLinkPlugin matchers={urlMatchers} />
          <FloatingLinkEditorPlugin container={containerRef.current} readOnly={disabled} />
          <KeyboardShortcutPlugin />
          <MarkdownShortcutPlugin transformers={transformers} />
          <TabIndentationPlugin />
          <CodeBlockExitPlugin />
          <ImagePasteBlockerPlugin />
          <MarkdownPastePlugin transformers={transformers} />
        </>
      ) : (
        <AutosizeTextarea
          className={classNames(
            'flex-1 w-full resize-none rounded-none border-0 bg-card px-4 py-3 text-sm text-foreground',
            'placeholder:text-foreground-subtle focus:outline-none focus-within:ring-transparent',
            'read-only:cursor-default',
          )}
          placeholder={placeholder}
          {...remainingPlainTextFieldParams}
          disabled={false}
          readOnly={disabled}
          value={plainTextValue ?? ''}
          onBlur={onPlainTextBlur}
          onChange={(e) => onPlainTextChange(e.target.value)}
        />
      )}
    </div>
  );
}

export const MarkdownEditor = ({ field, placeholder, onContentNormalized, disabled }: Props) => {
  // Track which tab is active: RICH_TEXT or PLAIN_TEXT
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.RICH_TEXT);

  const {
    value: plainTextValue,
    onChange: onPlainTextChange,
    onBlur: onPlainTextBlur,
    ...remainingPlainTextFieldParams
  } = field;

  const config = useMemo<InitialConfigType>(
    () =>
      createMarkdownEditorConfig('knowledgebase-markdown-editor', (error) => {
        console.error('Lexical error:', error?.message ?? error);
      }),
    [],
  );

  const transformers = useMemo(() => createMarkdownTransformers(), []);
  const urlMatchers = useMemo(() => createMarkdownUrlMatchers(), []);
  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <div
      className={classNames(
        'flex h-full min-h-[360px] max-h-[360px] lg:max-h-none flex-col overflow-hidden rounded-xl lg:rounded-b-none border border-border bg-card',
        disabled
          ? 'opacity-50'
          : 'focus-within:!border-ring focus-within:!ring-[3px] focus-within:ring-ring/25',
      )}
    >
      <LexicalComposer initialConfig={config}>
        <Toolbar viewMode={viewMode} onChangeView={setViewMode} disabled={disabled} />
        <EditorContent
          viewMode={viewMode}
          plainTextValue={plainTextValue}
          onPlainTextChange={onPlainTextChange}
          onPlainTextBlur={onPlainTextBlur}
          remainingPlainTextFieldParams={remainingPlainTextFieldParams}
          transformers={transformers}
          urlMatchers={urlMatchers}
          placeholder={placeholder}
          containerRef={containerRef}
          onContentNormalized={onContentNormalized}
          disabled={disabled}
        />
      </LexicalComposer>
    </div>
  );
};
